import {
  ArrowUpRight,
  CheckCircle2,
  Clock,
  Flame,
  HelpCircle,
  Loader2,
  Mail,
  Phone,
  Snowflake,
  Thermometer,
  User,
  XCircle,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useAuth } from '@/features/auth/hooks/useAuth'
import { getLeads as getAdminLeads } from '@/modules/admin/api/leads.api'
import { getAssignedLeads as getSalesLeads } from '@/modules/sales/api/sales.api'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { buildLeadFilterUrl } from '@/shared/lib/leadNavigation'
import { cn } from '@/shared/lib/utils'

const STATUS_CONFIG = {
  won: {
    label: 'Won',
    badgeClass: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    icon: CheckCircle2,
  },
  lost: {
    label: 'Lost',
    badgeClass: 'border-red-200 bg-red-50 text-red-700',
    icon: XCircle,
  },
  in_progress: {
    label: 'In Progress',
    badgeClass: 'border-amber-200 bg-amber-50 text-amber-700',
    icon: Clock,
  },
  open: {
    label: 'Open',
    badgeClass: 'border-sky-200 bg-sky-50 text-sky-700',
    icon: HelpCircle,
  },
}

export default function SalespersonLeadsModal({
  person,
  open,
  onOpenChange,
  period,
  customRange,
  leadsUrl = '/admin/leads',
}) {
  const navigate = useNavigate()
  const { role, user } = useAuth()
  const isSalesRole = role === 'sales' || user?.role === 'sales'

  const [activeTab, setActiveTab] = useState('all')
  const [allLeads, setAllLeads] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  const salesPersonId = person?.salesId || person?._id
  const target = Number(person?.target) || 0
  const achievedRevenue = Number(person?.revenue || person?.achievement || 0)
  const percentage =
    target > 0 ? Math.round((achievedRevenue / target) * 100) : 0
  const remainingValue = target - achievedRevenue

  useEffect(() => {
    if (!open || !salesPersonId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setAllLeads([])
      return
    }

    let isMounted = true
    const fetchSalespersonLeads = async () => {
      setIsLoading(true)
      try {
        const fetchFn = isSalesRole ? getSalesLeads : getAdminLeads
        const params = {
          limit: 1000,
          period: period === 'all' ? undefined : period || undefined,
          startDate: period === 'custom' ? customRange?.startDate : undefined,
          endDate: period === 'custom' ? customRange?.endDate : undefined,
        }
        if (!isSalesRole) {
          params.salesPerson = salesPersonId
        }

        const res = await fetchFn(params)
        if (isMounted) {
          const list = Array.isArray(res?.data)
            ? res.data
            : Array.isArray(res)
              ? res
              : []
          setAllLeads(list)
        }
      } catch (err) {
        console.error('Failed to load salesperson leads:', err)
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    fetchSalespersonLeads()

    return () => {
      isMounted = false
    }
  }, [open, salesPersonId, period, customRange, isSalesRole])

  // Count leads by status (using fetched leads if present, else fallback to person counts)
  const wonCount =
    allLeads.length > 0
      ? allLeads.filter((l) => l.status === 'won').length
      : Number(person?.won ?? person?.orders ?? 0)

  const lostCount =
    allLeads.length > 0
      ? allLeads.filter((l) => l.status === 'lost').length
      : Number(person?.lost ?? 0)

  const inProgressCount =
    allLeads.length > 0
      ? allLeads.filter((l) => l.status === 'in_progress').length
      : Number(person?.inProgress ?? 0)

  const openCount =
    allLeads.length > 0
      ? allLeads.filter((l) => l.status === 'open').length
      : Number(person?.open ?? 0)

  const totalAssignedCount =
    allLeads.length > 0
      ? allLeads.length
      : wonCount + lostCount + inProgressCount + openCount

  // Filter leads for the currently active tab
  const displayedLeads = useMemo(() => {
    if (activeTab === 'all') return allLeads
    return allLeads.filter((l) => l.status === activeTab)
  }, [allLeads, activeTab])

  if (!person) return null

  const targetLeadsUrl = isSalesRole ? '/sales/leads' : leadsUrl

  const handleNavigateToLeadsTable = (leadStatus) => {
    onOpenChange(false)
    navigate(
      buildLeadFilterUrl(targetLeadsUrl, {
        salesPerson: isSalesRole ? undefined : salesPersonId,
        status: leadStatus,
        period,
        customRange,
      }),
    )
  }

  const TABS = [
    { key: 'all', label: 'All Assigned', count: totalAssignedCount },
    { key: 'won', label: 'Won', count: wonCount },
    { key: 'lost', label: 'Lost', count: lostCount },
    { key: 'in_progress', label: 'In Progress', count: inProgressCount },
    { key: 'open', label: 'Open', count: openCount },
  ]

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-w-4xl flex-col p-0 sm:rounded-2xl h-[90dvh] sm:h-auto sm:max-h-[85vh] overflow-hidden">
        {/* Header with Salesperson Profile */}
        <div className="shrink-0 border-b border-slate-100 bg-slate-50/50 p-5 sm:p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-slate-900 text-base font-bold text-white shadow-sm">
                {person.name
                  ? person.name
                      .split(' ')
                      .map((p) => p[0])
                      .join('')
                      .slice(0, 2)
                      .toUpperCase()
                  : 'SP'}
              </div>
              <div>
                <DialogTitle className="text-lg font-bold text-slate-900">
                  {person.name}
                </DialogTitle>
                <p className="text-xs text-slate-500">
                  Salesperson Performance & Leads Breakdown
                </p>
              </div>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => handleNavigateToLeadsTable(activeTab)}
              className="gap-1.5 rounded-full text-xs font-semibold self-start sm:self-center"
            >
              <span>View Full Table</span>
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Button>
          </div>

          {/* Target & Achievement Stats Cards - fitted for compact modal */}
          <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-2">
            <div className="min-w-0 rounded-xl border border-slate-200/80 bg-white p-2 sm:px-2.5 sm:py-2.5 shadow-2xs flex flex-col justify-between overflow-hidden">
              <p className="text-[11px] font-semibold text-slate-500 leading-tight">
                Target Assigned
              </p>
              <p
                className="mt-1 text-xs sm:text-[11.5px] md:text-xs font-bold text-slate-900 tracking-tight truncate"
                title={`₹${target.toLocaleString('en-IN')}`}
              >
                ₹{target.toLocaleString('en-IN')}
              </p>
            </div>

            <div className="min-w-0 rounded-xl border border-emerald-200/80 bg-emerald-50/40 p-2 sm:px-2.5 sm:py-2.5 shadow-2xs flex flex-col justify-between overflow-hidden">
              <p className="text-[11px] font-semibold text-emerald-700 leading-tight">
                Achieved (Won)
              </p>
              <p
                className="mt-1 text-xs sm:text-[11.5px] md:text-xs font-bold text-emerald-600 tracking-tight truncate"
                title={`₹${achievedRevenue.toLocaleString('en-IN')}`}
              >
                ₹{achievedRevenue.toLocaleString('en-IN')}
              </p>
            </div>

            <div className="min-w-0 rounded-xl border border-slate-200/80 bg-white p-2 sm:px-2.5 sm:py-2.5 shadow-2xs flex flex-col justify-between overflow-hidden">
              <p className="text-[11px] font-semibold text-slate-500 leading-tight">
                Remaining Target
              </p>
              <p
                className="mt-1 text-xs sm:text-[11.5px] md:text-xs font-bold text-slate-900 tracking-tight truncate"
                title={`₹${remainingValue.toLocaleString('en-IN')}`}
              >
                {remainingValue < 0
                  ? `₹${Math.abs(remainingValue).toLocaleString('en-IN')}`
                  : `₹${remainingValue.toLocaleString('en-IN')}`}
              </p>
            </div>

            <div className="min-w-0 rounded-xl border border-slate-200/80 bg-white p-2 sm:px-2.5 sm:py-2.5 shadow-2xs flex flex-col justify-between overflow-hidden">
              <p className="text-[11px] font-semibold text-slate-500 leading-tight">
                Progress
              </p>
              <p
                className="mt-1 text-xs sm:text-[11.5px] md:text-xs font-bold text-slate-900 tracking-tight truncate"
                title={`${percentage.toLocaleString('en-IN')}%`}
              >
                {percentage.toLocaleString('en-IN')}%
              </p>
              <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all duration-500"
                  style={{
                    width: `${Math.min(100, Math.max(0, percentage))}%`,
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Tab Selection with Lead Counts */}
        <div className="shrink-0 flex items-center gap-1.5 overflow-x-auto border-b border-slate-200 bg-white px-5 sm:px-6 pt-2.5">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={cn(
                'relative flex items-center gap-1 pb-2.5 text-xs font-semibold transition-colors whitespace-nowrap',
                activeTab === tab.key
                  ? 'text-slate-950 font-bold'
                  : 'text-slate-500 hover:text-slate-700',
              )}
            >
              <span>{tab.label}</span>
              <span
                className={cn(
                  'rounded-full px-1.5 py-0.2 text-[10px] font-bold tabular-nums',
                  activeTab === tab.key
                    ? 'bg-slate-900 text-white'
                    : 'bg-slate-100 text-slate-600',
                )}
              >
                {tab.count}
              </span>
              {activeTab === tab.key && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full bg-slate-950" />
              )}
            </button>
          ))}
        </div>

        {/* Leads Content */}
        <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-5">
          {isLoading ? (
            <div className="flex h-48 flex-col items-center justify-center gap-2">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              <p className="text-xs text-slate-400">Loading leads...</p>
            </div>
          ) : displayedLeads.length === 0 ? (
            <div className="flex h-48 flex-col items-center justify-center text-center">
              <p className="text-sm font-medium text-slate-600">
                No {activeTab !== 'all' ? activeTab.replace('_', ' ') : ''}{' '}
                leads found
              </p>
              <p className="mt-1 text-xs text-slate-400">
                There are currently 0{' '}
                {activeTab !== 'all' ? activeTab.replace('_', ' ') : 'assigned'}{' '}
                leads for {person.name} ({totalAssignedCount} total assigned).
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white shadow-xs">
              {displayedLeads.map((lead) => {
                const statusConf =
                  STATUS_CONFIG[lead.status] || STATUS_CONFIG.open
                const StatusIcon = statusConf.icon

                return (
                  <div
                    key={lead._id}
                    onClick={() => handleNavigateToLeadsTable(lead.status)}
                    className="group flex cursor-pointer items-center justify-between p-3.5 transition hover:bg-slate-50/80"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate text-sm font-bold text-slate-900 group-hover:text-blue-600">
                          {lead.company || lead.name}
                        </span>
                        {lead.leadWarmth === 'hot' && (
                          <Flame className="size-3.5 text-orange-500" />
                        )}
                        {lead.leadWarmth === 'cold' && (
                          <Snowflake className="size-3.5 text-sky-500" />
                        )}
                        {lead.leadWarmth === 'warm' && (
                          <Thermometer className="size-3.5 text-amber-500" />
                        )}
                      </div>

                      <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                        {lead.name && lead.name !== lead.company && (
                          <span className="flex items-center gap-1">
                            <User className="size-3 text-slate-400" />
                            {lead.name}
                          </span>
                        )}
                        {lead.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="size-3 text-slate-400" />
                            {lead.phone}
                          </span>
                        )}
                        {lead.email && (
                          <span className="flex items-center gap-1">
                            <Mail className="size-3 text-slate-400" />
                            {lead.email}
                          </span>
                        )}
                      </div>

                      {lead.products?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {lead.products.map((p, idx) => (
                            <span
                              key={idx}
                              className="rounded-md bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600"
                            >
                              {p.product} (x{p.quantity})
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-3 pl-3">
                      <Badge
                        className={cn(
                          'gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold',
                          statusConf.badgeClass,
                        )}
                      >
                        <StatusIcon className="size-3" />
                        {statusConf.label}
                      </Badge>
                      <ArrowUpRight className="size-4 text-slate-300 transition group-hover:text-slate-600" />
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="shrink-0 flex items-center justify-between border-t border-slate-100 bg-slate-50 px-5 sm:px-6 py-3">
          <span className="text-xs text-slate-500">
            Showing {displayedLeads.length} of {displayedLeads.length}{' '}
            {activeTab === 'all' ? 'assigned' : activeTab.replace('_', ' ')}{' '}
            leads ({totalAssignedCount} total assigned)
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            className="rounded-full"
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
